import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generateColor } from '../utils/colors';
import { MapTooltip } from './MapTooltip';
import { EdgeLike, edgeToGeoJson } from '../utils/geoJsonUtils';
import {
  selectAllPathsChains,
  selectAllPathsOperations,
  selectAllPathsPickedChains,
  selectAllPathsVisibleChains,
} from '../scenarios/allPaths/allPathsSlice';
import * as pathsActions from '../paths/pathsSlice';
import { priceFormatter } from '../helpers/formatters';
import { selectAllNodes } from './slice/mapSelectors';
import { RootState } from '@/store';
import { AllPathsChainOption } from '@/types/api/AllPathsChainOption';
import { Node } from '@/types';
import { useMapLayer } from '../hooks/useMapLayer';
import { useMapContext, MapEventListener } from './context/MapContext';

const getEdgeStyle = () => [
  {
    filter: ['>', ['get', 'head'], 0],
    style: {
      'shape-points': 3,
      'shape-radius': ['get', 'arroheadSize'],
      'shape-fill-color': ['get', 'color'],
      'shape-stroke-color': ['get', 'color'],
      'shape-stroke-width': 4,
      'shape-rotate-with-view': false,
      'shape-displacement': [0, 0],
      'shape-opacity': 1,
      'shape-angle': ['get', 'angle'],
    },
  },
  {
    filter: ['all', ['==', ['get', 'lineDash'], 0], ['<=', ['get', 'head'], 0]],
    style: {
      'stroke-width': ['get', 'width'],
      'stroke-color': ['get', 'color'],
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
      // 'stroke-offset': -15,
      'shape-angle': 0,
    },
  },
  {
    filter: ['all', ['==', ['get', 'lineDash'], 1], ['<=', ['get', 'head'], 0]],
    style: {
      'stroke-line-dash': [15, 15, 5, 15],
      'stroke-width': ['get', 'width'],
      'stroke-color': ['get', 'color'],
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
      // 'stroke-offset': -15,
      'shape-angle': 0,
    },
  },
];

export const MapAllPathsChains = ({ zIndex }: { zIndex: number }) => {
  const {
    map,
    registerHoverListener,
    unregisterHoverListener,
    registerClickListener,
    unregisterClickListener,
  } = useMapContext();

  const dispatch = useDispatch();

  const chains = useSelector(selectAllPathsChains);
  const operations = useSelector(selectAllPathsOperations);
  const pickedChains = useSelector(selectAllPathsPickedChains);
  const nodes = useSelector(selectAllNodes);
  const visibleChains = useSelector(selectAllPathsVisibleChains);
  const selectedPathId = useSelector(
    (state: RootState) => state.paths.selectedPathId,
  );
  const selectedChain = useMemo(() => {
    return chains.find(
      ({ chain_group }) => chain_group[0].chain_number === selectedPathId,
    );
  }, [selectedPathId]);

  const [popupChainSymbol, setPopupChainSymbol] = useState<string | null>(null);
  const [popupChainId, setPopupChainId] = useState<number | null>(null);

  const popupChain = useMemo(() => {
    return popupChainId
      ? pickedChains?.find(({ chain_id }) => chain_id === popupChainId)
      : chains.find(({ symbol }) => symbol === popupChainSymbol)
          ?.chain_group?.[0];
  }, [popupChainSymbol, chains]);

  const hoverListener: MapEventListener = useCallback(
    (feature) => {
      if (feature?.getProperties()?.key === `all-paths-chain-${popupChainId}`)
        return;
      if (!feature?.getProperties()?.key?.startsWith('all-paths-chain-')) {
        setPopupChainSymbol(null);
        setPopupChainId(null);
        return;
      }
      const picked = feature?.getProperties()?.picked as boolean;
      const chainSymbol = feature?.getProperties()?.symbol;
      const chainId = feature?.getProperties()?.id;

      const chain = pickedChains.find((chain) =>
        picked
          ? chain.chain_id === chainId
          : chain.generic_symbol === chainSymbol,
      );
      setPopupChainSymbol(chain?.generic_symbol ?? null);
      setPopupChainId(picked ? chain?.chain_id ?? null : null);
    },
    [chains],
  );

  const clickListener: MapEventListener = useCallback(
    (feature) => {
      if (
        !feature ||
        !feature?.getProperties()?.key?.startsWith('all-paths-chain-')
      )
        return;
      if (
        feature?.getProperties()?.key ===
        `all-paths-chain-${selectedChain?.chain_group[0].generic_symbol}`
      )
        return;

      const chainId = feature.getProperties()?.id;
      const chain = pickedChains.find(
        (chain) => chain.chain_id.toString() === chainId?.toString(),
      );
      dispatch(
        pathsActions.pathSelected({
          selectedPathId: chain?.chain_number,
        }),
      );
      const pathBounds = feature.getGeometry()?.getExtent();
      if (pathBounds) {
        map?.getView().fit(pathBounds, {
          padding: [50, 50, 50, 50],
          duration: 1000,
        });
      }
    },
    [selectedChain, dispatch, map],
  );

  const chainOptionFeatures = useMemo(() => {
    const referenceChains: AllPathsChainOption[] = chains.map(
      ({ chain_group }) => chain_group[0],
    );
    return referenceChains
      .filter((chain) => visibleChains[chain.generic_symbol])
      .map((chain) => {
        const chainOperations = chain.op_list
          .map((opId) => operations[opId])
          .filter(Boolean);
        const pathPositions = chainOperations
          .filter((op) => Array.isArray(op.path_node_ids))
          .flatMap(
            (op) =>
              op.path_node_ids
                ?.map((nodeId) => {
                  const node = nodes[nodeId];
                  return node;
                })
                .filter(Boolean)
                .map((node) => {
                  return [Number(node.lat), Number(node.lng)];
                }) ?? [],
          );
        return {
          path: { positions: pathPositions },
          chain,
        };
      })
      .filter(({ path }) => path.positions?.length > 1)
      .flatMap(({ path, chain }) => {
        const color = generateColor(chain.chain_number, 60, 60);
        return edgeToGeoJson(
          path as EdgeLike,
          () => ({
            id: chain.chain_id,
            symbol: chain.generic_symbol,
            picked: false,
            key: `all-paths-chain-${chain.generic_symbol}`,
            lineDash: 1,
            color,
            width: 10,
            arroheadSize: 13,
          }),
          true,
          { frequency: 0.8 },
        );
      });
  }, [nodes, chains, operations, visibleChains]);

  const pickedChainFeatures = useMemo(() => {
    return pickedChains
      .filter(
        (chain) =>
          visibleChains[chain.chain_id] ||
          chain.chain_number === selectedPathId,
      )
      .sort((a, b) =>
        a.chain_number === selectedPathId
          ? 1
          : b.chain_number === selectedPathId
          ? -1
          : 0,
      )
      .map((chain) => {
        const chainOperations = chain.operations;
        const pathnodes = (chainOperations
          .filter((op) => Array.isArray(op.path_node_ids))
          .flatMap((op) =>
            op.path_node_ids
              ?.map((nodeId) => {
                const node = nodes[nodeId];
                return node;
              })
              .filter(Boolean),
          ) ?? []) as Node[];
        const pathPositions =
          pathnodes.map((node) => {
            return [Number(node.lat), Number(node.lng)] as [number, number];
          }) ?? [];
        return {
          path: { positions: pathPositions },
          chain,
        };
      })
      .filter(({ path }) => path.positions?.length > 1)
      .flatMap(({ path, chain }) => {
        const color = generateColor(chain.chain_number, 80, 60);
        return edgeToGeoJson(
          path as EdgeLike,
          () => ({
            id: chain.chain_id,
            symbol: chain.generic_symbol,
            picked: true,
            key: `all-paths-chain-${chain.chain_id}`,
            lineDash: 0,
            color,
            width: selectedPathId === chain.chain_number ? 14 : 10,
            arroheadSize: selectedPathId === chain.chain_number ? 16 : 13,
          }),
          true,
          { frequency: 0.8 },
        );
      });
  }, [nodes, visibleChains, pickedChains, selectedPathId]);

  const features = useMemo(() => {
    return chainOptionFeatures.concat(pickedChainFeatures);
  }, [chainOptionFeatures, pickedChainFeatures]);

  const style = useMemo(() => getEdgeStyle(), []);

  useMapLayer(features, { zIndex, style });

  useEffect(() => {
    if (!map) return;
    registerClickListener(clickListener);
    registerHoverListener(hoverListener);
    return () => {
      unregisterClickListener(clickListener);
      unregisterHoverListener(hoverListener);
    };
  }, [map]);

  return (
    <MapTooltip visible={Boolean(popupChainSymbol)}>
      <div className="bg-dark p-2 px-3 border-dark text-light rounded">
        {Boolean(popupChainSymbol) && (
          <div className="d-flex flex-column">
            <div className="fs-6 fw-bold">Chain #{popupChain?.chain_id}</div>
            <div>
              $/dth: {priceFormatter.format(Number(popupChain?.profit_per_dth))}
            </div>
            <div>
              Volume: {Number(popupChain?.constrained_max_volume).toFixed(0)}
              dth
            </div>
            <div>
              Profit: {priceFormatter.format(Number(popupChain?.profit))}
            </div>
          </div>
        )}
      </div>
    </MapTooltip>
  );
};
